import { HttpParams } from '@angular/common/http';
import {
	ContestacaoTemplate,
	ContestacaoTemplateConfig,
	ContestacaoTemplateHistoricEntry,
	ContestacaoTemplateStatus,
	ContestacaoTemplateTopicEntry,
	ContestacaoTemplateTopicEntryInput,
	ContestacaoTemplateWithHistoric,
	CreateContestacaoTemplateDTO,
	UpdateContestacaoTemplateDTO,
} from 'services/contestacao-template/contestacao-template.model';
import {
	ProductCustomContestacaoListItem,
	ProductCustomContestacaoTopicSummary,
} from 'services/product-custom-contestacao/product-custom-contestacao.model';
import { AuditUser } from 'services/user/user.model';
import { MockHttpError, newMockId, nowIso } from '../mock-api';
import {
	MOCK_AUDIT_USER,
	MOCK_PEOPLE,
	MOCK_VERTICAL_INFO,
	productSetupIdOf,
} from '../shared/mock-context.const';
import { currentMockUser } from './contestacao-user';
import {
	buildTopics,
	CLIENT_CONFIG,
	daysAgoIso,
	fullStructure,
	GLOBAL_CONFIG,
	slugify,
	topicEntry,
} from './contestacao.const';

/**
 * Templates versionados da Contestação, em memória. Um "grupo" (familyId) reúne todas as
 * versões de um mesmo template; o `historic` devolvido pelo find é o grupo inteiro.
 */
interface StoredTemplate {
	familyId: string;
	template: ContestacaoTemplate;
}

const BRANCH_AVIACAO = { id: 'branch-1', name: 'Aviação' };
const CLIENT_SETUP = productSetupIdOf('trabalhista', 'contestacao');

function verticalOf(verticalId: string | null) {
	const info = Object.values(MOCK_VERTICAL_INFO).find((vertical) => vertical.id === verticalId);
	return info
		? { id: info.id, name: info.name, metadata: { color: info.color, background: info.background } }
		: null;
}

interface SeedOptions {
	id: string;
	familyId: string;
	name: string;
	description: string;
	status: ContestacaoTemplateStatus;
	version: number;
	basedOnVersionId: string | null;
	productSetupId: string | null;
	verticalId: string;
	branch: { id: string; name: string } | null;
	config: ContestacaoTemplateConfig;
	structure: { topicId: string; thesisIds: string[] }[];
	createdDaysAgo: number;
	updatedDaysAgo: number;
	publishedDaysAgo?: number;
	archivedDaysAgo?: number;
	author: AuditUser;
}

function seed(options: SeedOptions): StoredTemplate {
	return {
		familyId: options.familyId,
		template: {
			id: options.id,
			name: options.name,
			slug: slugify(options.name),
			description: options.description,
			status: options.status,
			config: options.config,
			version: options.version,
			basedOnVersionId: options.basedOnVersionId,
			productSetupId: options.productSetupId,
			verticalId: options.verticalId,
			branchId: options.branch?.id ?? null,
			isGlobal: options.productSetupId === null,
			publishedAt:
				options.publishedDaysAgo !== undefined ? daysAgoIso(options.publishedDaysAgo, 15) : null,
			archivedAt:
				options.archivedDaysAgo !== undefined ? daysAgoIso(options.archivedDaysAgo, 15) : null,
			createdAt: daysAgoIso(options.createdDaysAgo, 9),
			createdBy: options.author,
			updatedAt: daysAgoIso(options.updatedDaysAgo, 16, 20),
			updatedBy: options.author,
			vertical: verticalOf(options.verticalId),
			branch: options.branch,
			topics: buildTopics(options.structure, options.id),
		},
	};
}

const FULL = fullStructure();
/** v1 do cliente: ainda sem o tópico de danos morais e sem banco de horas / labor noturno */
const CLIENT_V1_STRUCTURE = [
	{ topicId: 'topic-001', thesisIds: ['thesis-001'] },
	{ topicId: 'topic-002', thesisIds: ['thesis-002'] },
	{ topicId: 'topic-003', thesisIds: ['thesis-003'] },
	{ topicId: 'topic-004', thesisIds: ['thesis-006', 'thesis-007'] },
	{ topicId: 'topic-006', thesisIds: ['thesis-010'] },
];
/** Rascunho v3: danos morais sobe para antes das verbas rescisórias */
const CLIENT_V3_STRUCTURE = [FULL[0], FULL[1], FULL[2], FULL[4], FULL[3], FULL[5]];

const TRABALHISTA_ID = MOCK_VERTICAL_INFO['trabalhista'].id;
const CIVEL_ID = MOCK_VERTICAL_INFO['civel'].id;

const templates: StoredTemplate[] = [
	// ── Cliente (product setup trabalhista/contestação) ──
	seed({
		id: 'tpl-ct-client-v1',
		familyId: 'fam-client',
		name: 'Contestação Trabalhista Padrão',
		description: 'Primeira versão do modelo de defesa trabalhista da empresa.',
		status: 'ARCHIVED',
		version: 1,
		basedOnVersionId: null,
		productSetupId: CLIENT_SETUP,
		verticalId: TRABALHISTA_ID,
		branch: BRANCH_AVIACAO,
		config: CLIENT_CONFIG,
		structure: CLIENT_V1_STRUCTURE,
		createdDaysAgo: 120,
		updatedDaysAgo: 110,
		publishedDaysAgo: 110,
		archivedDaysAgo: 45,
		author: MOCK_PEOPLE.gerente,
	}),
	seed({
		id: 'tpl-ct-client-v2',
		familyId: 'fam-client',
		name: 'Contestação Trabalhista Padrão',
		description: 'Inclui danos morais, banco de horas e labor noturno.',
		status: 'PUBLISHED',
		version: 2,
		basedOnVersionId: 'tpl-ct-client-v1',
		productSetupId: CLIENT_SETUP,
		verticalId: TRABALHISTA_ID,
		branch: BRANCH_AVIACAO,
		config: CLIENT_CONFIG,
		structure: FULL,
		createdDaysAgo: 50,
		updatedDaysAgo: 45,
		publishedDaysAgo: 45,
		author: MOCK_PEOPLE.gerente,
	}),
	seed({
		id: 'tpl-ct-client-v3',
		familyId: 'fam-client',
		name: 'Contestação Trabalhista Padrão',
		description: 'Rascunho: danos morais antes das verbas rescisórias.',
		status: 'DRAFT',
		version: 3,
		basedOnVersionId: 'tpl-ct-client-v2',
		productSetupId: CLIENT_SETUP,
		verticalId: TRABALHISTA_ID,
		branch: BRANCH_AVIACAO,
		config: CLIENT_CONFIG,
		structure: CLIENT_V3_STRUCTURE,
		createdDaysAgo: 2,
		updatedDaysAgo: 1,
		author: MOCK_PEOPLE.gerente,
	}),

	// ── Globais (admin LBCA) ──
	seed({
		id: 'tpl-gl-trab-v1',
		familyId: 'fam-gl-trab',
		name: 'Modelo LBCA — Contestação Trabalhista',
		description: 'Modelo base de contestação trabalhista mantido pela LBCA.',
		status: 'ARCHIVED',
		version: 1,
		basedOnVersionId: null,
		productSetupId: null,
		verticalId: TRABALHISTA_ID,
		branch: null,
		config: GLOBAL_CONFIG,
		structure: CLIENT_V1_STRUCTURE,
		createdDaysAgo: 200,
		updatedDaysAgo: 190,
		publishedDaysAgo: 190,
		archivedDaysAgo: 60,
		author: MOCK_AUDIT_USER,
	}),
	seed({
		id: 'tpl-gl-trab-v2',
		familyId: 'fam-gl-trab',
		name: 'Modelo LBCA — Contestação Trabalhista',
		description: 'Atualizado com a Reforma Trabalhista e o art. 223-G da CLT.',
		status: 'PUBLISHED',
		version: 2,
		basedOnVersionId: 'tpl-gl-trab-v1',
		productSetupId: null,
		verticalId: TRABALHISTA_ID,
		branch: null,
		config: GLOBAL_CONFIG,
		structure: FULL,
		createdDaysAgo: 65,
		updatedDaysAgo: 60,
		publishedDaysAgo: 60,
		author: MOCK_AUDIT_USER,
	}),
	seed({
		id: 'tpl-gl-trab-v3',
		familyId: 'fam-gl-trab',
		name: 'Modelo LBCA — Contestação Trabalhista',
		description: 'Rascunho em revisão pela equipe jurídica.',
		status: 'DRAFT',
		version: 3,
		basedOnVersionId: 'tpl-gl-trab-v2',
		productSetupId: null,
		verticalId: TRABALHISTA_ID,
		branch: null,
		config: GLOBAL_CONFIG,
		structure: CLIENT_V3_STRUCTURE,
		createdDaysAgo: 5,
		updatedDaysAgo: 4,
		author: MOCK_AUDIT_USER,
	}),
	seed({
		id: 'tpl-gl-aviacao-v1',
		familyId: 'fam-gl-aviacao',
		name: 'Modelo LBCA — Aviação (aeronautas)',
		description: 'Defesa para aeronautas e aeroviários, com foco em jornada e verbas rescisórias.',
		status: 'PUBLISHED',
		version: 1,
		basedOnVersionId: null,
		productSetupId: null,
		verticalId: TRABALHISTA_ID,
		branch: BRANCH_AVIACAO,
		config: GLOBAL_CONFIG,
		structure: [FULL[0], FULL[2], FULL[3], FULL[5]],
		createdDaysAgo: 90,
		updatedDaysAgo: 30,
		publishedDaysAgo: 30,
		author: MOCK_AUDIT_USER,
	}),
	seed({
		id: 'tpl-gl-civel-v1',
		familyId: 'fam-gl-civel',
		name: 'Modelo LBCA — Contestação Cível',
		description: 'Estrutura enxuta para contestações cíveis.',
		status: 'PUBLISHED',
		version: 1,
		basedOnVersionId: null,
		productSetupId: null,
		verticalId: CIVEL_ID,
		branch: null,
		config: GLOBAL_CONFIG,
		structure: [FULL[0], FULL[1], FULL[5]],
		createdDaysAgo: 75,
		updatedDaysAgo: 70,
		publishedDaysAgo: 70,
		author: MOCK_AUDIT_USER,
	}),
];

// ── Acesso ───────────────────────────────────────────────────────────────────

function stored(id: string): StoredTemplate {
	const entry = templates.find((item) => item.template.id === id);
	if (!entry)
		throw new MockHttpError(404, 'Template não encontrado', `Não existe template com id ${id}.`);
	return entry;
}

function family(familyId: string): StoredTemplate[] {
	return templates.filter((item) => item.familyId === familyId);
}

function historicOf(familyId: string): ContestacaoTemplateHistoricEntry[] {
	return family(familyId)
		.map(({ template }) => ({
			id: template.id,
			version: template.version,
			status: template.status,
			description: template.description ?? '',
			updatedAt: template.updatedAt,
			createdAt: template.createdAt,
			createdBy: template.createdBy,
		}))
		.sort((first, second) => second.version - first.version);
}

export function findTemplate(id: string): ContestacaoTemplateWithHistoric {
	const entry = stored(id);
	return structuredClone({ ...entry.template, historic: historicOf(entry.familyId) });
}

/** Versão publicada usada para montar revisões novas do product setup */
export function activeTemplateFor(productSetupId: string): ContestacaoTemplate {
	const published = (item: StoredTemplate) => item.template.status === 'PUBLISHED';
	const own = templates.find(
		(item) => item.template.productSetupId === productSetupId && published(item),
	);
	const fallback = templates.find(
		(item) =>
			item.template.isGlobal && published(item) && item.template.verticalId === TRABALHISTA_ID,
	);
	const chosen = own ?? fallback ?? templates[1];
	return structuredClone(chosen.template);
}

function touch(template: ContestacaoTemplate) {
	template.updatedAt = nowIso();
	template.updatedBy = currentMockUser();
}

function resolveTopics(
	inputs: ContestacaoTemplateTopicEntryInput[],
	previous: ContestacaoTemplateTopicEntry[],
	entryPrefix: string,
): ContestacaoTemplateTopicEntry[] {
	return [...inputs]
		.sort((first, second) => first.order - second.order)
		.map((input, index) => {
			const thesisIds = [...input.theses]
				.sort((first, second) => first.order - second.order)
				.map((thesis) => thesis.thesisId);
			const resolved = topicEntry(input.topicId, index, thesisIds, entryPrefix);
			// Tópicos/teses criados na demo (fora do roster) mantêm o nome que já tinham
			const previousTopic = previous.find((topic) => topic.id === input.topicId);
			if (previousTopic && resolved.name === 'Tópico')
				Object.assign(resolved, { name: previousTopic.name, slug: previousTopic.slug });
			resolved.theses = resolved.theses.map((thesis) => {
				const previousThesis = previous
					.flatMap((topic) => topic.theses)
					.find((item) => item.id === thesis.id);
				return previousThesis && thesis.name === 'Tese'
					? { ...previousThesis, order: thesis.order }
					: thesis;
			});
			return resolved;
		});
}

function assertDraft(template: ContestacaoTemplate) {
	if (template.status !== 'DRAFT') {
		throw new MockHttpError(
			409,
			'Versão não editável',
			'Apenas versões em rascunho podem ser alteradas ou removidas.',
		);
	}
}

// ── Mutações ─────────────────────────────────────────────────────────────────

export function createTemplate(dto: CreateContestacaoTemplateDTO): ContestacaoTemplate {
	const id = newMockId('tpl');
	const now = nowIso();
	const user = currentMockUser();
	const branch =
		dto.branchId === BRANCH_AVIACAO.id
			? BRANCH_AVIACAO
			: dto.branchId
				? { id: dto.branchId, name: 'Filial' }
				: null;
	const productSetupId = dto.productSetupId ?? null;
	const verticalId = dto.verticalId ?? (productSetupId ? TRABALHISTA_ID : null);
	const template: ContestacaoTemplate = {
		id,
		name: dto.name,
		slug: slugify(dto.name),
		description: dto.description ?? null,
		status: 'DRAFT',
		config: dto.config ?? (productSetupId ? CLIENT_CONFIG : GLOBAL_CONFIG),
		version: 1,
		basedOnVersionId: null,
		productSetupId,
		verticalId,
		branchId: branch?.id ?? null,
		isGlobal: productSetupId === null,
		publishedAt: null,
		archivedAt: null,
		createdAt: now,
		createdBy: user,
		updatedAt: now,
		updatedBy: user,
		vertical: verticalOf(verticalId),
		branch,
		topics: resolveTopics(dto.topicEntries ?? [], [], id),
	};
	templates.unshift({ familyId: newMockId('fam'), template });
	return structuredClone(template);
}

export function updateTemplate(
	id: string,
	dto: UpdateContestacaoTemplateDTO & { verticalId?: string | null },
): ContestacaoTemplate {
	const { template } = stored(id);
	assertDraft(template);
	if (dto.name !== undefined) {
		template.name = dto.name;
		template.slug = slugify(dto.name);
	}
	if (dto.description !== undefined) template.description = dto.description;
	if (dto.config !== undefined) template.config = dto.config;
	if (dto.verticalId !== undefined) {
		template.verticalId = dto.verticalId;
		template.vertical = verticalOf(dto.verticalId);
	}
	if (dto.topicEntries)
		template.topics = resolveTopics(dto.topicEntries, template.topics, template.id);
	touch(template);
	return structuredClone(template);
}

export function deleteTemplate(id: string): null {
	const entry = stored(id);
	assertDraft(entry.template);
	templates.splice(templates.indexOf(entry), 1);
	return null;
}

/** DRAFT → TESTING → PUBLISHED. Ao publicar, a versão publicada anterior do grupo é arquivada. */
export function promoteTemplate(id: string): ContestacaoTemplate {
	const entry = stored(id);
	const { template } = entry;
	if (template.status === 'DRAFT') {
		template.status = 'TESTING';
	} else if (template.status === 'TESTING') {
		for (const sibling of family(entry.familyId)) {
			if (sibling.template.status === 'PUBLISHED') {
				sibling.template.status = 'ARCHIVED';
				sibling.template.archivedAt = nowIso();
			}
		}
		template.status = 'PUBLISHED';
		template.publishedAt = nowIso();
	}
	touch(template);
	return structuredClone(template);
}

/** TESTING → DRAFT; PUBLISHED → TESTING */
export function demoteTemplate(id: string): ContestacaoTemplate {
	const { template } = stored(id);
	if (template.status === 'TESTING') template.status = 'DRAFT';
	else if (template.status === 'PUBLISHED') {
		template.status = 'TESTING';
		template.publishedAt = null;
	} else
		throw new MockHttpError(
			409,
			'Não é possível rebaixar esta versão',
			'Somente versões em teste ou publicadas podem ser rebaixadas.',
		);
	touch(template);
	return structuredClone(template);
}

/** Cria um rascunho (nova versão do grupo) a partir da versão informada */
export function cloneTemplate(id: string): ContestacaoTemplate {
	const entry = stored(id);
	const siblings = family(entry.familyId);
	if (siblings.some((sibling) => sibling.template.status === 'DRAFT')) {
		throw new MockHttpError(
			409,
			'Já existe um rascunho',
			'Conclua ou descarte o rascunho atual antes de criar uma nova versão.',
		);
	}
	const now = nowIso();
	const user = currentMockUser();
	const cloneId = newMockId('tpl');
	const source = structuredClone(entry.template);
	const clone: ContestacaoTemplate = {
		...source,
		id: cloneId,
		status: 'DRAFT',
		version: Math.max(...siblings.map((sibling) => sibling.template.version)) + 1,
		basedOnVersionId: source.id,
		publishedAt: null,
		archivedAt: null,
		createdAt: now,
		createdBy: user,
		updatedAt: now,
		updatedBy: user,
		topics: source.topics.map((topic) => ({
			...topic,
			topicEntryId: `${cloneId}-${topic.id}`,
			theses: topic.theses.map((thesis) => ({
				...thesis,
				thesisEntryId: `${cloneId}-${thesis.id}`,
			})),
		})),
	};
	templates.unshift({ familyId: entry.familyId, template: clone });
	return structuredClone(clone);
}

// ── Listagens ────────────────────────────────────────────────────────────────

function booleanParam(query: HttpParams, key: string): boolean | undefined {
	const value = query.get(key);
	return value === null ? undefined : value === 'true';
}

function filterTemplates(query: HttpParams): ContestacaoTemplate[] {
	const search = query.get('search')?.toLowerCase().trim();
	const status = query.get('status');
	const productSetupId = query.get('productSetupId');
	const verticalId = query.get('verticalId');
	const includeGlobal = booleanParam(query, 'includeGlobal');
	const global = booleanParam(query, 'global');
	const sortBy = query.get('sortBy') ?? 'updatedAt';

	return templates
		.map((item) => item.template)
		.filter((template) => {
			if (global === true && !template.isGlobal) return false;
			if (global === false && template.isGlobal) return false;
			if (
				productSetupId &&
				template.productSetupId !== productSetupId &&
				!(includeGlobal && template.isGlobal)
			)
				return false;
			if (status && template.status !== status) return false;
			if (verticalId && template.verticalId !== verticalId) return false;
			if (search && !template.name.toLowerCase().includes(search)) return false;
			return true;
		})
		.sort((first, second) =>
			sortBy === 'name'
				? first.name.localeCompare(second.name, 'pt-BR')
				: second.updatedAt.localeCompare(first.updatedAt),
		);
}

export function listTemplates(query: HttpParams): ContestacaoTemplate[] {
	return structuredClone(filterTemplates(query));
}

function topicSummary(topic: ContestacaoTemplateTopicEntry): ProductCustomContestacaoTopicSummary {
	return {
		id: topic.id,
		name: topic.name,
		slug: topic.slug,
		order: topic.order,
		description: topic.description,
		productSetupId: topic.productSetupId,
		isGlobal: topic.isGlobal,
		enableBatchApproval: topic.enableBatchApproval,
		createdAt: topic.createdAt,
		createdBy: topic.createdBy?.name ?? null,
		updatedAt: topic.updatedAt,
		updatedBy: topic.updatedBy?.name ?? null,
		deletedAt: null,
		deletedBy: null,
	};
}

export function listCustomTemplates(query: HttpParams): ProductCustomContestacaoListItem[] {
	return filterTemplates(query).map((template) => ({
		id: template.id,
		productSetupId: template.productSetupId,
		verticalId: template.verticalId ?? '',
		name: template.name,
		slug: template.slug,
		version: template.version,
		status: template.status,
		description: template.description,
		isGlobal: template.isGlobal,
		createdAt: template.createdAt,
		createdBy: template.createdBy?.name ?? null,
		updatedAt: template.updatedAt,
		updatedBy: template.updatedBy?.name ?? null,
		topics: template.topics.map(topicSummary),
	}));
}
